import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Play } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { RevoraCard } from "./RevoraCard";
import { Phone } from "./Phone";

export const Hero = () => {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-screen bg-white noise overflow-hidden flex items-center pt-[72px]" data-testid="hero-section">
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-volt/[0.06] blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex items-center gap-2 border border-black/10 rounded-full px-4 py-1.5 mb-10"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-volt animate-pulse" />
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-black/60">NFC Tap-to-Review Products</span>
          </motion.div>

          <h1 className="font-display font-black tracking-tighter leading-[0.92] text-6xl sm:text-7xl lg:text-8xl text-ink" data-testid="hero-headline">
            <MaskedLine delay={0.25}>Tap<span className="text-volt">.</span></MaskedLine>
            <MaskedLine delay={0.4}>Review<span className="text-volt">.</span></MaskedLine>
            <MaskedLine delay={0.55}><span className="text-outline">Done.</span></MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 text-lg text-black/60 max-w-md leading-relaxed"
            data-testid="hero-subcopy"
          >
            Give your customers a faster, easier way to leave a Google review.
            Every Revora stand, plate and card is uniquely configured for your business before it ships.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#products")}
              className="group inline-flex items-center gap-3 bg-volt text-ink font-bold px-8 py-4 rounded-full hover:bg-voltdark hover:text-white transition-colors duration-300"
            >
              Get Your Revora Products
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#how-it-works")}
              className="group inline-flex items-center gap-3 border border-black/15 text-ink font-bold px-8 py-4 rounded-full hover:border-ink transition-colors duration-300"
            >
              <Play className="w-4 h-4 fill-ink" />
              See How It Works
            </button>
          </motion.div>
        </div>

        <div className="relative h-[480px] sm:h-[560px] hidden md:block" data-testid="hero-visual">
          {!reduceMotion && (
            <div className="absolute left-6 top-24 w-[380px] h-[380px] pointer-events-none" aria-hidden="true" data-testid="signal-rings">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="absolute inset-0 rounded-full border border-volt/30"
                  animate={{ scale: [0.55, 1.15], opacity: [0.6, 0] }}
                  transition={{ duration: 3.6, repeat: Infinity, delay: i * 1.2, ease: "easeOut" }}
                />
              ))}
            </div>
          )}
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: -8 }}
            animate={{ opacity: 1, y: 0, rotate: -6 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: cardY }}
            className="absolute left-0 top-16 w-[340px] lg:w-[400px]"
          >
            <RevoraCard />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: phoneY }}
            className="absolute right-0 top-0"
          >
            <motion.div
              animate={{ x: [0, -26, -26, 0], y: [0, 14, 14, 0] }}
              transition={{ duration: 5, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
            >
              <Phone screen="review" businessName="Your Business" />
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            className="absolute left-[300px] lg:left-[360px] top-[240px] flex items-center gap-2 bg-ink text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-volt" /> One tap away
          </motion.div>
        </div>
      </div>
    </section>
  );
};
